import React from "react";
import ReactDOM from "react-dom/client";
// Self-hosted scheme catalogue. Imported here rather than <link>ed in
// index.html so Vite fingerprints the .woff2 into this bundle; Vite still
// emits a plain <link rel="stylesheet"> in the built HTML, so the fonts are
// not blocked behind JS execution.
//
// RELATIVE PATH ON PURPOSE - do not "tidy" this into `@shared/...`. The
// standalone Cloudflare Pages build (foyer-app/vite.config.mts, the one that
// actually ships) aliases @shared to foyer-shared/dist, which is tsc output:
// no .css, no .woff2. The alias would resolve to nothing and take the fonts,
// or the whole build, down with it.
import "../shared/fonts/scheme.css";
import App from "./App";
import ProbeChordHost from "./components/ProbeChordHost";
import { registerOfflineShell } from "./lib/offlineShell";
import { installTizenKeyBridge } from "./lib/tizenKeys";
import { logTizenInventory } from "./lib/tizenInventory";
import { refreshTizenInstalledApps } from "./lib/foyerBridge";
import { installForegroundWatch } from "./lib/foregroundWatch";
import SelfHealBoundary from "./components/SelfHealBoundary";
import { repairHostDocumentUrl } from "./lib/hostReload";
import { installUncaughtErrorCapture } from "./lib/uncaughtErrors";
import { installPageLifecycle } from "./lib/pageLifecycle";

// FIRST, before anything that could ever reload the page. Every WGT up to and
// including 0.0.8 calls `history.replaceState(null, "", "?tv=1")` AFTER
// appending `<base href="https://foyer.nenda.com/">`, so on firmware that
// honours it the widget's URL reads as the REMOTE app URL - and every
// `location.reload()` in this app (pairing restart, unpair, 401, web_bundle
// OTA, checkout wipe, self-heal) would then navigate off the widget, away from
// its storage and `window.tizen`. Putting the URL back here makes all of those
// safe at once, on fielded panels, with no WGT reinstall. No-op everywhere else.
// See lib/hostReload.ts.
repairHostDocumentUrl();

// Uncaught errors and unhandled rejections: into the console (so the ring
// buffer and the log shipper carry them, with this screen's context) AND to
// the fleet-wide client-error groups. Before anything else can throw.
installUncaughtErrorCapture();

// Did the previous page end cleanly, and how big is this one's JS heap?
// Tizen has no exit-reason API, so a crash and a restart look the same from
// outside; this leaves a `[lifecycle]` line for the log shipper to carry.
// See lib/pageLifecycle.ts.
installPageLifecycle();

// Samsung's remote speaks keyCodes, not W3C key names, so Back was never
// reaching the handlers that were written for it - and Exit closed the app out
// from under the guest (TIZEN-15). Installed BEFORE render so the very first
// keypress on the pairing screen is already translated. No-op off Tizen.
installTizenKeyBridge();

// THEN the inventory - the order on these two lines is load-bearing.
//
// This call used to live at App.tsx's top level, and the first log capture off
// a real panel came back reading `registerKey=not attempted`. That was the
// reporting lying, not the TV refusing: ES module imports are evaluated before
// the importing module's body, so App.tsx's top level ran while THIS file was
// still loading - before the line above had claimed a single key. Keeping both
// calls here, in sequence, is what makes the report describe reality.
logTizenInventory();

// Ask the panel which applications it actually has, before React renders. A
// `tizenId` in the CMS says an operator INTENDED a tile; this says whether the
// television can honour it, and a Netflix tile on a signage display that has no
// Netflix is a button with nothing behind it. Answers in ~100ms - well inside
// the config fetch - and until it does, nothing is filtered. No-op off Tizen.
refreshTizenInstalledApps();

// Notice when the television stops showing us. Measured on a panel: Exit does
// not close Foyer, it switches the SOURCE - so the page stays alive, the
// heartbeat keeps going out, and the console shows a green device in a room
// looking at "No signal". This reports that; it deliberately does not fight for
// the screen back. See lib/foregroundWatch.ts.
installForegroundWatch();

// Offline shell (public/sw.js). PROD only - in dev a worker caching the Vite
// module graph causes far more confusion than it prevents. Registered before
// render so a first-ever boot warms the shell as early as possible; it can
// never block or fail the render.
if (import.meta.env.PROD) {
  registerOfflineShell();
}

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/* Up Up Down Down Left Right opens the platform probe. The Samsung widget
        has no address bar, and this panel's remote delivers arrows but not the
        colour keys or digits, so those six are the ones a shortcut can be built
        on. Prefer "Capture logs" from the CMS - see lib/tizenInventory.ts. */}
    {/* Outermost, so it catches a throw from ANYTHING below - including the
        probe. A dead render tree on an unattended panel is a black screen
        nobody is coming to fix; this reloads out of it on a backoff. No visible
        fallback on purpose: an error card looks like a product decision and
        does not get reported, a black screen looks like a fault and does. */}
    <SelfHealBoundary>
      <ProbeChordHost>
        <App />
      </ProbeChordHost>
    </SelfHealBoundary>
  </React.StrictMode>
);
